/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { useState, useEffect } from "react"
import { CMSLink } from "frontend/src/components/CMSLink"
import { SectionGrid, Grid, Flex } from "frontend/src/components/Layout"
import Img from "gatsby-image"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import { BsChevronRight, BsChevronLeft, BsCircleFill, BsCircle } from "react-icons/bs"
import { Carousel } from "react-responsive-carousel"
import "react-responsive-carousel/lib/styles/carousel.min.css"
import { BlockText } from "frontend/src/components/BlockText"
import { isDarkColor } from "frontend/src/utils/isDarkColor"
/* eslint-disable react-hooks/exhaustive-deps */

const arrowStyles = {
  position: "absolute",
  zIndex: 2,
  top: "calc(50% - 15px)",
  width: "30",
  height: "30",
  cursor: "pointer",
  border: "none",
  background: "transparent",
  outline: "none",
  "&:active": {
    outline: "none",
  },
}

const indicatorStyles = {
  background: "transparent !important",
  margin: "0px",
  display: "inline-block",
  border: "none",
  outline: "none",
  "&:active": {
    outline: "none",
  },
}

const getConfigurableProps = (milliseconds, numberOfSlides) => ({
  showArrows: numberOfSlides > 1 ? true : false, // show arrows at left and right edge of the slide
  showStatus: false, // don't show the number of the slide at the top right of the slide
  showIndicators: numberOfSlides > 1 ? true : false, // show dots at the bottom of the slide
  infiniteLoop: numberOfSlides > 1 ? true : false, // restart with first slide after last slide
  showThumbs: false, // don't show thumbnail images below the slides
  useKeyboardArrows: numberOfSlides > 1 ? true : false, // use left and right arrow on the keyboard to switch slide
  autoPlay: numberOfSlides > 1 ? true : false, // switch slides automatically (cf. slide show)
  stopOnHover: numberOfSlides > 1 ? true : false, // stop slide show when hovering slides
  swipeable: numberOfSlides > 1 ? true : false, // switch slides by swiping (especially on mobile devices)
  dynamicHeight: false,
  emulateTouch: true,
  selectedItem: 0, // slide to be shown initially
  interval: milliseconds, // time to show one slide before switching
  transitionTime: 150, // time to switch slides
  swipeScrollTolerance: 5, // maximal vertical motion during swiping
  renderArrowPrev: (onClickHandler, hasPrev, label) =>
    hasPrev && (
      <button
        type="button"
        onClick={onClickHandler}
        title={label}
        sx={{ ...arrowStyles, left: "32px" }}
      >
        <BsChevronLeft size="3em" sx={{ color: "almostBlack" }} />
      </button>
    ),
  renderArrowNext: (onClickHandler, hasNext, label) =>
    hasNext && (
      <button
        type="button"
        onClick={onClickHandler}
        title={label}
        sx={{ ...arrowStyles, right: "32px" }}
      >
        <BsChevronRight size="3em" sx={{ color: "almostBlack" }} />
      </button>
    ),
  renderIndicator: (onClickHandler, isSelected, index, label) => {
    if (isSelected) {
      return (
        <button type="button" sx={{ ...indicatorStyles }}>
          <BsCircleFill sx={{ color: "almostBlack" }} />
        </button>
      )
    } else {
      return (
        <button type="button" onClick={onClickHandler} title={label} sx={{ ...indicatorStyles }}>
          <BsCircle sx={{ color: "almostBlack" }} />
        </button>
      )
    }
  },
})

function SimpleSliderDesktop({ milliseconds, slides }) {
  return (
    <Carousel
      {...getConfigurableProps(milliseconds, slides.length)}
      sx={{ width: "100%", height: "600px", background: "background" }}
    >
      {slides.map((slide, index) => (
        <DesktopSlide slide={slide} key={index} sx={{ width: "100%", height: "600px" }} />
      ))}
    </Carousel>
  )
}

function SimpleSliderMobile({ milliseconds, slides }) {
  const [wrapperHeight, setWrapperHeight] = useState("unset")
  return (
    <Carousel
      {...getConfigurableProps(milliseconds, slides.length)}
      sx={{ width: "100%", height: `${wrapperHeight} !important` }}
    >
      {slides.map((slide, index) => (
        <MobileSlide
          slide={slide}
          key={index}
          wrapperHeight={wrapperHeight}
          setWrapperHeight={setWrapperHeight}
          sx={{ width: "100%", height: wrapperHeight }}
        />
      ))}
    </Carousel>
  )
}

function DesktopSlide({ slide }) {
  const { backgroundDesktop, title, uppercaseTitle, text, button, buttonColor, buttonType } = slide
  return (
    <SectionGrid
      sx={{
        width: "100%",
        height: "600px",
        display: ["none", "none", "grid"],
        backgroundColor: "background",
      }}
    >
      <Img
        fluid={getFluidFromAsset(backgroundDesktop, 4000)}
        sx={{
          gridColumn: "1 / span 5",
          gridRow: "1 / span 1",
          width: "100%",
          height: "600px",
          // maxHeight: "700px",
        }}
        imgStyle={{ width: "100%", height: "600px" }}
      />
      <Grid
        columns="1fr 1fr"
        sx={{ gridColumn: "2 / span 3", gridRow: "1 / span 1", width: "100%", height: "100%" }}
      >
        <Flex
          sx={{
            gridColumn: "1 / span 1",
            gridRow: "1 / span 1",
            height: "600px",
            ml: 5,
          }}
          direction="column"
          justify="center"
          align="flex-start"
          wrap={false}
        >
          {uppercaseTitle ? (
            <div sx={{ marginBottom: "8px" }}>
              <BlockText
                blocks={title}
                style={{
                  zIndex: 5,
                  fontSize: `62px`,
                  fontWeight: "medium",
                  lineHeight: `60px !important`,
                  textAlign: "left",
                  textTransform: "uppercase",
                  my: 0,
                }}
              />
            </div>
          ) : (
            <BlockText
              blocks={title}
              style={{
                zIndex: 5,
                fontSize: "62px",
                fontWeight: "medium",
                lineHeight: "60px !important",
                textAlign: "left",
                textTransform: "none",
                my: "0px",
              }}
            />
          )}
          <BlockText
            style={{
              fontSize: "22px",
              lineHeight: "32px",
              fontWeight: "light",
              zIndex: 5,
              mt: 0,
              mb: 8,
              textAlign: "left",
            }}
            blocks={text}
          />
          <div sx={{ textAlign: "left", width: "100%", mt: 4, mb: 5 }}>
            <CMSLink
              sx={{
                py: 5,
                px: 7,
                textAlign: "center",
                bg: buttonType === "full" ? buttonColor : "transparent",
                borderWidth: ["2px", "3px"],
                borderColor: buttonType === "outlined" ? buttonColor : "transparent",
                borderStyle: buttonType === "outlined" ? "solid" : "none",
                color:
                  buttonType === "full"
                    ? isDarkColor(buttonColor)
                      ? "white"
                      : "black"
                    : buttonColor,
                fontFamily: "body",
                textTransform: "uppercase",
                fontSize: "16px",
                lineHeight: "16px",
                fontWeight: "regular",
                "&:hover": {
                  opacity: buttonType === "outlined" ? 0.6 : 1,
                  backgroundColor:
                    buttonType === "full"
                      ? isDarkColor(buttonColor)
                        ? "white"
                        : "black"
                      : "transparent",
                  color:
                    buttonType === "full"
                      ? isDarkColor(buttonColor)
                        ? "black"
                        : "white"
                      : buttonColor,
                },
              }}
              link={button}
            />
          </div>
        </Flex>
      </Grid>
    </SectionGrid>
  )
}

function MobileSlide({ slide, wrapperHeight, setWrapperHeight }) {
  const {
    backgroundMobile,
    title,
    uppercaseTitle,
    text,
    button,
    buttonType,
    buttonColor,
    backgroundColor,
  } = slide
  // const [imageWidth, setImageWidth] = useState("375px")
  // const [imageHeight, setImageHeight] = useState("unset")
  /*useEffect(() => {
    const getSizes = () => {
      try {
        // const max = window.innerWidth
        // const ar = backgroundMobile.asset.metadata.dimensions.aspectRatio
        // const imageheight = max / ar
        const height = `${imageheight}px`
        setImageWidth(`${max}px`)
        setImageHeight(height)
      } catch (error) {
        setImageWidth("375px")
        setImageHeight("unset")
      }
    }
    try {
      window.addEventListener("load", getSizes)
      window.addEventListener("resize", getSizes)
      getSizes()
    } catch (error) {
      console.error(error)
    }
  }, [])*/
  return (
    <div
      sx={{
        width: "100%",
        display: ["grid", "grid", "none"],
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        backgroundColor: backgroundColor,
      }}
    >
      <Img
        fluid={getFluidFromAsset(backgroundMobile, 2000)}
        sx={{
          width: "100%",
          height: "auto",
          gridColumn: "1 / span 1",
          gridRow: "1 / span 1",
          maxHeight: ["unset", "700px"],
        }}
        imgStyle={{
          width: "100%",
          maxHeight: ["unset", "700px"],
          objectFit: "cover",
          objectPosition: "50% top",
        }}
      />
      <div
        sx={{
          width: "100%",
          zIndex: 5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          gridColumn: "1 / span 1",
          gridRow: "1 / span 1",
          px: 4,
        }}
      >
        {uppercaseTitle ? (
          <BlockText
            blocks={title}
            style={{
              zIndex: 5,
              textAlign: "center",
              textTransform: "uppercase",
              my: 0,
              lineHeight: "117%",
            }}
          />
        ) : (
          <BlockText
            blocks={title}
            style={{
              zIndex: 5,
              textAlign: "center",
              my: 0,
              py: 0,
              lineHeight: "117%",
              textTransform: "none",
            }}
          />
        )}
        <BlockText
          style={{
            fontSize: "20px",
            lineHeight: "133%",
            zIndex: 5,
            mt: 3,
            mb: 5,
            textAlign: "center",
          }}
          blocks={text}
        />
        <div sx={{ textAlign: "center", width: "100%", mt: 4, mb: 8, zIndex: 5 }}>
          <CMSLink
            sx={{
              pt: 3,
              pb: 3,
              px: 8,
              maxHeight: "60px",
              display: "inline-block",
              textAlign: "center",
              bg: buttonType === "full" ? buttonColor : "transparent",
              borderWidth: ["2px", "3px"],
              borderColor: buttonType === "outlined" ? buttonColor : "transparent",
              borderStyle: buttonType === "outlined" ? "solid" : "none",
              color:
                buttonType === "full"
                  ? isDarkColor(buttonColor)
                    ? "white"
                    : "black"
                  : buttonColor,
              fontFamily: "body",
              textTransform: "uppercase",
              zIndex: 1000,
              "&:hover": {
                opacity: buttonType === "outlined" ? 0.6 : 1,
                backgroundColor:
                  buttonType === "full"
                    ? isDarkColor(buttonColor)
                      ? "white"
                      : "black"
                    : "transparent",
                color:
                  buttonType === "full"
                    ? isDarkColor(buttonColor)
                      ? "black"
                      : "white"
                    : buttonColor,
              },
            }}
            link={button}
          />
        </div>
      </div>
    </div>
  )
}

function HeroTextCta({ delay, slides }) {
  const milliseconds = delay === 0 ? 86400000 : delay * 1000
  useEffect(() => {
    const triggerScript = () => {
      window.pushowl.trigger("getCurrentPermission").then(permission => {
        if (permission !== "default") return
        window.pushowl.trigger("showWidget", { type: "browserPrompt" })
      })
      localStorage.setItem("pushowl-optin", "true")
    }
    setTimeout(() => {
      const pushOwlOptin = localStorage.getItem("pushowl-optin")
      if (!pushOwlOptin) {
        triggerScript()
      }
    }, 30000)
  }, [])
  return (
    <SectionGrid>
      <div
        sx={{ display: ["none", "none", "block"], gridColumn: ["unset", "unset", "1 / span 5"] }}
      >
        <SimpleSliderDesktop milliseconds={milliseconds} slides={slides} />
      </div>
      <div
        sx={{ display: ["block", "block", "none"], gridColumn: ["unset", "unset", "1 / span 5"] }}
      >
        <SimpleSliderMobile milliseconds={milliseconds} slides={slides} />
      </div>
    </SectionGrid>
  )
}

export { HeroTextCta }
